import type { TableCopyMethod } from '@/analytics/enums/table.enums'
import { useCallback, useRef, useState, type ReactNode } from 'react'
import TableCopyPill from './TableCopyPill'
import { Popover, PopoverAnchor, PopoverContent } from '@/components/popover/Popover'
import { useDebouncedEffect } from '@/hooks/useDebouncedEffect'
import { useIsTouchOnlyDevice } from '@/hooks/useIsTouchOnlyDevice'
import HoverableCellWrapper from '@/components/hoverable-cell-wrapper/HoverableCellWrapper'

/** Shows cell editing controls on hover while keeping cell contents interactive. */
export default function TableCellEditMenu({
  children,
  enabled,
  onEdit,
  onCopy,
  allowFootnotes = false,
  hideActions = false,
  selected = false,
}: {
  children: ReactNode
  allowFootnotes?: boolean
  enabled: boolean
  onEdit: () => void
  onCopy?: (footnotes: boolean, method?: TableCopyMethod) => Promise<void>
  hideActions?: boolean
  selected?: boolean
}) {
  const [copyMenuOpen, setCopyMenuOpen] = useState(false)
  const [hovered, setHovered] = useState(false)
  const [open, setOpen] = useState(false)
  const [focused, setFocused] = useState(false)
  const actionsEnabled = !hideActions && (enabled || Boolean(onCopy))
  const touchOnly = useIsTouchOnlyDevice()
  const cellAnchor = useRef<{ getBoundingClientRect: () => DOMRect; contextElement: HTMLElement } | null>(null)
  const [tableViewport, setTableViewport] = useState<HTMLElement | null>(null)
  const setCellAnchor = useCallback((element: HTMLDivElement | null) => {
    const cell = element?.closest('td') ?? element
    const table = cell?.closest('table')
    setTableViewport(table?.parentElement ?? null)
    cellAnchor.current = cell
      ? {
          contextElement: cell,
          getBoundingClientRect: () => {
            const bounds = cell.getBoundingClientRect()
            const headerBottom = table?.tHead?.getBoundingClientRect().bottom
            const viewportBounds = table?.parentElement?.getBoundingClientRect()
            // Keep fully obscured cells detached so their controls stay hidden.
            if (viewportBounds && bounds.bottom <= (headerBottom ?? viewportBounds.top)) {
              return new DOMRect(bounds.left, viewportBounds.top - 1, bounds.width, 0)
            }
            // Anchor to the visible portion of a cell beneath the sticky header.
            const top = Math.max(bounds.top, headerBottom ?? bounds.top)
            const left = Math.max(bounds.left, viewportBounds?.left ?? bounds.left)
            const right = Math.min(bounds.right, viewportBounds?.right ?? bounds.right)
            return right > left ? new DOMRect(left, top, right - left, Math.max(0, bounds.bottom - top)) : bounds
          },
        }
      : null
  }, [])
  useDebouncedEffect(() => {
    if (!touchOnly) setOpen(hovered || focused)
  }, [hovered, focused, touchOnly])

  const startEdit = () => {
    if (!enabled) return
    setHovered(false)
    setOpen(false)
    onEdit()
  }

  return (
    <HoverableCellWrapper onCellHover={(hovered) => setHovered(actionsEnabled && hovered)} onCellDoubleClick={enabled ? startEdit : undefined}>
      <Popover open={actionsEnabled && (open || selected || copyMenuOpen)} onOpenChange={setOpen}>
        <PopoverAnchor virtualRef={cellAnchor} />
        <div
          ref={setCellAnchor}
          className="pointer-events-auto"
          onMouseEnter={() => actionsEnabled && !touchOnly && setHovered(true)}
          onMouseLeave={() => !touchOnly && setHovered(false)}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          onDoubleClick={(event) => {
            if (event.target instanceof Element && event.target.closest('a, button')) return
            startEdit()
          }}
          onClick={(event) => {
            if (event.target instanceof Element && event.target.closest('a, button')) {
              setHovered(false)
              setOpen(false)
              return
            }
            if (actionsEnabled && touchOnly) setOpen(true)
          }}
        >
          {children}
        </div>
        <PopoverContent
          aria-label="Cell actions"
          width="auto"
          showArrow={false}
          side="top"
          align="end"
          alignOffset={6}
          sideOffset={-6}
          collisionPadding={8}
          collisionBoundary={tableViewport}
          sticky="always"
          hideWhenDetached={!!tableViewport}
          updatePositionStrategy="always"
          className="rounded bg-white p-0 shadow-none ring-1 ring-inset ring-brand-neutral-300"
          onMouseEnter={() => !touchOnly && setHovered(true)}
          onMouseLeave={() => !touchOnly && setHovered(false)}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          onOpenAutoFocus={(event) => event.preventDefault()}
          onCloseAutoFocus={(event) => event.preventDefault()}
        >
          <TableCopyPill onMenuOpenChange={setCopyMenuOpen} allowFootnotes={allowFootnotes} onEdit={enabled ? startEdit : undefined} onCopy={onCopy} />
        </PopoverContent>
      </Popover>
    </HoverableCellWrapper>
  )
}
